import React from 'react';
import { ArrowClockwise, CaretRight, Info, LockSimple, WarningOctagon, X } from '@phosphor-icons/react';

export const cx = (...items) => items.filter(Boolean).join(' ');

export function readable(value, fallback = 'Not available') {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value).replaceAll('_', ' ');
}

export function PageShell({
  title,
  purpose,
  context,
  actions,
  status,
  children,
  testId,
  width = 'max-w-[1380px]',
  spacing = 'space-y-6',
  headerClassName = '',
  embedded = false,
}) {
  return (
    <div data-testid={testId} className={cx('mx-auto w-full px-4 sm:px-6 lg:px-8 py-6', spacing, width)}>
      {embedded ? (
        <div className={cx('flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between', headerClassName)}>
          <div className="min-w-0">
            <h2 className="font-heading text-2xl font-bold text-[var(--ink-950)]">{title}</h2>
            {purpose && <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-[var(--ink-600)]">{purpose}</p>}
            {context && <div className="mt-1 text-xs text-[var(--ink-500)]">{context}</div>}
            {status && <div className="mt-3">{status}</div>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      ) : (
        <PageHeader title={title} purpose={purpose} context={context} actions={actions} status={status} className={headerClassName} />
      )}
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  purpose,
  subtitle,
  context,
  badge,
  actions,
  status,
  testId,
  className = '',
}) {
  const desc = purpose || subtitle;
  return (
    <header
      data-testid={testId || 'canonical-surface-header'}
      className={cx('flex flex-col gap-4 border-b border-slate-200/80 pb-6 lg:flex-row lg:items-start lg:justify-between', className)}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="break-words font-heading text-2xl font-bold leading-[1.15] tracking-tight text-[var(--ink-950,#05070D)] sm:text-3xl lg:text-[32px]">
            {title}
          </h1>
          {badge && <div className="shrink-0">{badge}</div>}
        </div>
        {desc && <p className="mt-2 max-w-3xl text-[14.5px] leading-relaxed text-[var(--ink-600,#5B6472)]">{desc}</p>}
        {context && <div className="mt-1.5 text-[13px] text-[var(--ink-500,#7B8494)]">{context}</div>}
        {status && <div className="mt-2.5">{status}</div>}
      </div>
      {actions && (
        <div className="flex w-full shrink-0 flex-wrap items-center gap-2.5 lg:w-auto lg:justify-end">
          {actions}
        </div>
      )}
    </header>
  );
}

export const SurfaceHeader = PageHeader;

export function SectionHeader({ title, description, actions }) {
  return (
    <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        <h2 className="break-words font-heading text-[20px] font-bold leading-snug tracking-tight text-[var(--ink-950,#05070D)]">{title}</h2>
        {description && <p className="mt-1 text-[14px] leading-relaxed text-[var(--ink-600,#5B6472)]">{description}</p>}
      </div>
      {actions && <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">{actions}</div>}
    </div>
  );
}

export function InfoCard({ title, description, actions, children, testId, className = '' }) {
  return (
    <section data-testid={testId} className={cx('tkp-card p-4', className)}>
      {(title || description || actions) && <SectionHeader title={title} description={description} actions={actions} />}
      {children}
    </section>
  );
}

export const SectionCard = InfoCard;

export function KpiStrip({ items = [], columns = 'md:grid-cols-4' }) {
  return (
    <div className={cx('grid grid-cols-1 rounded-lg border border-[var(--line,#D9DEE7)] bg-white divide-y md:divide-y-0 md:divide-x divide-[var(--line-soft,#EEF1F5)]', columns)}>
      {items.map((item) => <MetricCard key={item.label} {...item} />)}
    </div>
  );
}

export function MetricCard({ label, value, help, tone = 'neutral', icon: Icon, testId }) {
  const dotClass = {
    good: 'bg-[var(--risk-low)]',
    warn: 'bg-[var(--risk-moderate)]',
    bad: 'bg-[var(--risk-critical)]',
    info: 'bg-[var(--status-info)]',
  }[tone];
  return (
    <div data-testid={testId} className="relative min-h-[96px] min-w-0 p-4 transition-colors hover:bg-slate-50/50">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-700)]">{label}</div>
        <div className="flex items-center gap-2">
          {dotClass && <span className={cx('h-1.5 w-1.5 rounded-full', dotClass)} aria-hidden />}
          {Icon && <Icon size={16} weight="bold" className="text-[var(--ink-400,#667085)]" aria-hidden />}
        </div>
      </div>
      <div className="mt-2.5 break-words font-heading text-[1.5rem] font-semibold leading-tight text-[var(--ink-950)]">{readable(value, '-')}</div>
      {help && <div className="mt-1 text-xs leading-5 text-[var(--ink-600)]">{help}</div>}
    </div>
  );
}


export function StatusBadge({ value = 'unknown', label, className = '' }) {

  const normalized = String(value || 'unknown').toLowerCase();
  const neutral = ['disabled', 'not_configured', 'not configured', 'optional_disabled', 'unavailable', 'proposed', 'candidate', 'draft', 'pending_validation', 'boundary_only'];

  const good = [
    'ok',
    'pass',
    'ready',
    'healthy',
    'online',
    'connected',
    'operational',
    'available',
    'configured',
    'active',
    'completed',
    'ready_for_final_polish',
    'accepted',
    'validated',
    'contained',
    'redacted',
    'tenant_internal',
    'aggregate_only',
    'enforced',
  ];
  const warn = [
    'warning',
    'warn',
    'manual',
    'manual_command_available',
    'degraded',
    'optional',
    'unknown',
    'ready_with_conditions',
    'submitted',
    'steward_review',
    'needs_review',
    'needs review',
    'human_review',
    'review',
    'planned',
    'preview',
    'future',
    'demo_ready',
    'api_ready',
    'api ready',
    'boardroom_fallback',
    'started',
    'in_progress',
  ];
  const cls = neutral.includes(normalized)
    ? 'border-[var(--line)] bg-[var(--surface-sunken)] text-[var(--ink-700)]'
    : good.includes(normalized)
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : warn.includes(normalized)
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-rose-200 bg-rose-50 text-rose-800';

  return (
    <span className={cx('inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest', cls, className)}>
      {label || readable(value)}
    </span>
  );
}

export function HealthBadge({ value = 'unknown', label }) {
  return <StatusBadge value={value} label={label} />;
}

export function SeverityBadge({ value = 'LOW' }) {
  const level = String(value || 'LOW').toUpperCase();
  const cls = {
    CRITICAL: 'sev-CRITICAL',
    HIGH: 'sev-HIGH',
    MODERATE: 'sev-MODERATE',
    LOW: 'sev-LOW',
  }[level] || 'border-slate-200 bg-slate-50 text-slate-600';
  return <span className={cx('inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest', cls)}>{level}</span>;
}

export function ActionButton({ children, variant = 'secondary', className = '', disabledReason, ...props }) {
  const disabled = props.disabled || Boolean(disabledReason);
  const cls = variant === 'primary' ? 'tkp-btn-primary' : variant === 'danger' ? 'tkp-btn-danger' : 'tkp-btn-secondary';
  return (
    <button
      type="button"
      {...props}
      disabled={disabled}
      title={disabledReason || props.title}
      className={cx(cls, 'inline-flex items-center justify-center gap-2', className)}
    >
      {children}
    </button>
  );
}

export function ButtonGroup({ children, align = 'left', className = '' }) {
  return <div className={cx('flex flex-wrap items-center gap-2', align === 'right' && 'justify-end', className)}>{children}</div>;
}

export function EmptyState({ title = 'Nothing to show yet', description, action }) {
  return (
    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-center">
      <Info size={22} weight="duotone" className="mx-auto text-slate-400" aria-hidden />
      <div className="mt-3 font-semibold text-slate-900">{title}</div>
      {description && <div className="mx-auto mt-1 max-w-xl text-sm leading-6 text-slate-600">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading...' }) {
  return (
    <div className="tkp-card p-5" role="status" aria-live="polite" aria-label={label}>
      <div className="space-y-3" aria-hidden="true">
        <div className="h-3 w-28 animate-pulse rounded-sm bg-slate-200" />
        <div className="h-5 w-3/5 animate-pulse rounded-sm bg-slate-100" />
        <div className="h-3 w-4/5 animate-pulse rounded-sm bg-slate-100" />
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function SkeletonCard() {
  return <div className="tkp-card h-32 animate-pulse bg-slate-100" aria-hidden />;
}

export function SkeletonBlock({ className = 'h-24' }) {
  return <div className={cx('animate-pulse rounded-md bg-slate-100', className)} aria-hidden />;
}

export function ErrorState({ title = 'Something needs attention', message, onRetry }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-5 text-rose-900" role="alert">
      <div className="flex items-start gap-3">
        <WarningOctagon size={20} weight="duotone" className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{title}</div>
          {message && <div className="mt-1 text-sm leading-6">{message}</div>}
          {onRetry && <RetryButton onClick={onRetry} className="mt-3" />}
        </div>
      </div>
    </div>
  );
}

export function RetryButton({ onClick, className = '' }) {
  return (
    <button type="button" onClick={onClick} className={cx('tkp-btn-secondary inline-flex items-center gap-2', className)}>
      <ArrowClockwise size={14} weight="bold" /> Retry
    </button>
  );
}

export function DataTable({ columns, rows, rowKey = 'id', empty, onRowClick, testId }) {
  return (
    <div data-testid={testId} className="overflow-hidden rounded-md border border-slate-200 bg-white">
      {!!rows.length && (
        <div className="divide-y divide-slate-100 md:hidden">
          {rows.map((row) => (
            <article
              key={row[rowKey] || JSON.stringify(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row);
                }
              } : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? 'button' : undefined}
              className={cx('p-4 text-sm', onRowClick && 'cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none')}
            >
              <div className="space-y-3">
                {columns.map((col) => (
                  <div key={col.key} className="grid grid-cols-[minmax(84px,0.36fr)_minmax(0,1fr)] gap-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{col.label}</div>
                    <div className="min-w-0 text-slate-800 [&_*]:max-w-full" style={{ overflowWrap: 'anywhere' }}>
                      {col.render ? col.render(row) : readable(row[col.key])}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
      <div className="hidden overflow-x-auto md:block" tabIndex="0" aria-label="Scrollable data table">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {columns.map((col) => <th key={col.key} className={cx('px-3 py-2.5', col.className)}>{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row[rowKey] || JSON.stringify(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx('border-b border-slate-100 text-sm last:border-b-0', onRowClick && 'cursor-pointer hover:bg-slate-50')}
              >
                {columns.map((col) => <td key={col.key} className={cx('px-3 py-3 align-top', col.cellClassName)}>{col.render ? col.render(row) : readable(row[col.key])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && <div className="p-5">{empty || <EmptyState />}</div>}
    </div>
  );
}

export function FilterBar({ children }) {
  return <div className="tkp-card grid grid-cols-1 items-end gap-3 p-3 md:grid-cols-[minmax(140px,0.7fr)_repeat(3,minmax(180px,1fr))]">{children}</div>;
}

export function DetailDrawer({ title, open, onClose, children }) {
  const panelRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const closeHandler = React.useRef(onClose);
  closeHandler.current = onClose;

  React.useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeHandler.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = panelRef.current?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      trigger?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40" role="dialog" aria-modal="true" aria-label={title}>
      <button className="absolute inset-0 cursor-default" aria-label="Close details" onClick={onClose} />
      <aside ref={panelRef} className="relative flex h-[100dvh] w-full max-w-[520px] flex-col overflow-hidden bg-white shadow-2xl">
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-slate-200 bg-white p-4 sm:p-5">
          <div className="min-w-0 break-words font-heading text-lg font-bold leading-tight">{title}</div>
          <button ref={closeRef} type="button" onClick={onClose} className="tkp-icon-btn" aria-label="Close details"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {children}
        </div>
      </aside>
    </div>
  );
}

export function ConfirmDialog({ open, title, description, onCancel, onConfirm, confirmLabel = 'Confirm' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/40 p-3 pt-8 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-2xl sm:p-6">
        <div className="font-heading text-xl font-bold tracking-tight">{title}</div>
        {description && <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>}
        <div className="mt-5 grid grid-cols-1 gap-2 sm:flex sm:justify-end">
          <ActionButton onClick={onCancel} className="w-full sm:w-auto">Cancel</ActionButton>
          <ActionButton variant="primary" onClick={onConfirm} className="w-full sm:w-auto">{confirmLabel}</ActionButton>
        </div>
      </div>
    </div>
  );
}

export function Tooltip({ label, children }) {
  return <span title={label}>{children}</span>;
}

export function ActionBar({ children }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

export function Breadcrumbs({ items = [] }) {
  return (
    <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500" aria-label="Breadcrumb">
      {items.map((item, idx) => (
        <React.Fragment key={item.label}>
          <span className={idx === items.length - 1 ? 'font-semibold text-slate-800' : ''}>{item.label}</span>
          {idx < items.length - 1 && <CaretRight size={12} aria-hidden />}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function Stepper({ steps, currentId, onSelect }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2" aria-label="Workflow stages">
      <div className="grid min-w-0 grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-7">
        {steps.map((step, idx) => {
          const active = step.id === currentId;
          const stateClass = active
            ? 'border-slate-950 bg-slate-950 text-white'
            : step.complete
              ? 'border-emerald-200 bg-emerald-50 text-emerald-950 hover:border-emerald-400'
              : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-400';
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onSelect?.(step)}
              aria-current={active ? 'step' : undefined}
              className={cx('min-h-[66px] w-full min-w-0 rounded-sm border px-2.5 py-2 text-left focus:outline-none focus:ring-2 focus:ring-slate-400', stateClass)}
            >
              <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-[0.08em]">
                <span>Stage {idx + 1}</span>
                {step.meta && <span>{step.meta}</span>}
              </div>
              <div className="tkp-line-clamp-2 mt-1 text-[13px] font-semibold leading-4">{step.label || step.name}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const ProgressStepper = Stepper;

export function WorkflowStageCard({ title, purpose, status, current, metrics, actions, children, testId }) {
  return (
    <section data-testid={testId} className={cx('rounded-md border bg-white p-4', current ? 'border-slate-950 shadow-md ring-2 ring-slate-950/10' : 'border-slate-200')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-lg font-black leading-tight tracking-tight text-slate-950">{title}</div>
          {purpose && <p className="mt-2 text-sm leading-6 text-slate-600">{purpose}</p>}
        </div>
        <StatusBadge value={status} />
      </div>
      {metrics && <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">{metrics}</div>}
      {children && <div className="mt-4">{children}</div>}
      {actions && <div className="mt-4">{actions}</div>}
    </section>
  );
}

export function WorkflowTaskCard({ title, why, evidence, status, meta, action, testId }) {
  return (
    <article data-testid={testId} className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
        <div className="min-w-0">
          <div className="tkp-line-clamp-3 font-semibold leading-snug text-slate-950">{title}</div>
          {why && <p className="mt-2 text-sm leading-6 text-slate-600">{why}</p>}
        </div>
        <StatusBadge value={status} />
      </div>
      {evidence && (
        <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-2 text-xs leading-5 text-slate-600">
          <span className="font-semibold text-slate-900">Expected evidence:</span> {evidence}
        </div>
      )}
      {meta && <div className="mt-3 flex flex-wrap gap-1.5">{meta}</div>}
      {action && <div className="mt-3">{action}</div>}
    </article>
  );
}

export function PrivacyNotice({ children = 'Project Climate and workflow signals are aggregate-only. TALKTO+ does not profile, rank, or score individuals.', variant = 'compact' }) {
  if (variant === 'banner') {
    return <div className="inline-flex max-w-3xl rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm leading-6 text-sky-900">{children}</div>;
  }
  return (
    <div className="inline-flex max-w-full items-start gap-2 text-xs font-medium leading-5 text-[var(--ink-400)]">
      <LockSimple size={13} weight="duotone" className="mt-0.5 shrink-0" aria-hidden />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export function DemoModeBanner() {
  return <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600">Synthetic demo data</span>;
}

export function DemoNotice({ children = 'Demo-safe synthetic data. Do not enter secrets or real customer data.' }) {
  return <div className="rounded-md border border-[var(--line)] bg-[var(--surface-sunken)] px-4 py-3 text-sm leading-6 text-[var(--ink-700)]">{children}</div>;
}

export function FeatureStatusCard({ title, status = 'optional', description }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="font-semibold text-slate-950">{title}</div>
        <StatusBadge value={status} />
      </div>
      {description && <div className="mt-2 text-sm leading-6 text-slate-600">{description}</div>}
    </div>
  );
}

export function DisabledFeatureNotice({ title, reason }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
      <div className="font-semibold text-slate-950">{title}</div>
      <div className="mt-1 leading-6">{reason}</div>
    </div>
  );
}

export function ProgressRing({
  value,
  size = 48,
  strokeWidth = 4,
  tone = 'neutral',
  label,
  testId,
  className = '',
}) {
  const isNumeric = typeof value === 'number' && !Number.isNaN(value);
  const clamped = isNumeric ? Math.max(0, Math.min(100, value)) : null;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = isNumeric ? circumference - (clamped / 100) * circumference : circumference;

  const strokeColor = {
    good: 'var(--risk-low, #059669)',
    warn: 'var(--risk-moderate, #d97706)',
    bad: 'var(--risk-critical, #dc2626)',
    info: 'var(--brand-primary, #2563eb)',
    neutral: 'var(--slate-400, #94a3b8)',
  }[tone] || 'var(--slate-400, #94a3b8)';

  return (
    <div data-testid={testId} className={cx('inline-flex items-center gap-3', className)}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="var(--slate-200, #e2e8f0)"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {isNumeric && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              fill="transparent"
              style={{ transition: 'stroke-dashoffset 0.35s ease' }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-bold text-[var(--ink-900, #0f172a)]">
          {isNumeric ? `${Math.round(clamped)}%` : '--'}
        </div>
      </div>
      {label && (
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-[var(--ink-800, #1e293b)]">{label}</div>
        </div>
      )}
    </div>
  );
}


export function InsightAlert({
  title,
  description,
  children,
  tone = 'info',
  action,
  testId,
  className = '',
}) {
  const toneStyles = {
    info: 'border-blue-200 bg-blue-50/70 text-blue-950',
    warn: 'border-amber-200 bg-amber-50/70 text-amber-950',
    critical: 'border-rose-200 bg-rose-50/70 text-rose-950',
    good: 'border-emerald-200 bg-emerald-50/70 text-emerald-950',
  }[tone] || 'border-slate-200 bg-slate-50 text-slate-900';

  return (
    <div
      data-testid={testId}
      role="alert"
      className={cx('rounded-md border p-3.5 sm:p-4', toneStyles, className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {title && <div className="text-xs font-bold uppercase tracking-wider">{title}</div>}
          {description && <div className="mt-1 text-xs leading-relaxed">{description}</div>}
          {children && <div className="mt-1 text-xs leading-relaxed">{children}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

export function StatusCard({
  label,
  value,
  status,
  secondary,
  testId,
  className = '',
}) {
  return (
    <div
      data-testid={testId}
      className={cx(
        'rounded-md border border-[var(--line, #e2e8f0)] bg-white p-4 shadow-sm transition hover:border-[var(--slate-300, #cbd5e1)]',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-600, #475569)]">
          {label}
        </span>
        {status && <StatusBadge value={status} />}
      </div>
      <div className="mt-2.5 font-heading text-xl font-bold tracking-tight text-[var(--ink-950, #05070d)]">
        {readable(value, '-')}
      </div>
      {secondary && (
        <div className="mt-1 text-xs text-[var(--ink-500, #64748b)] truncate">
          {secondary}
        </div>
      )}
    </div>
  );
}
